/**
 * RTK Listener Middleware that intercepts every `setPendingSelection` dispatch
 * and performs the full merge → resolve → prune → commit cycle.
 *
 * All helper functions (merge, resolve, prune) are pure, independently
 * unit-testable, and exported so other modules can import them without
 * importing the listener itself.
 *
 * Execution order (all synchronous, before any React re-render):
 *   1. mergeSelections          — pending overrides current committed selection
 *   2. resolveConfigForSelection — look up the flat CONFIG_SCHEMA for this screen
 *   3. buildActiveGroupSnapshot  — extract available IDs from bootstrapped data
 *   4. pruneInvalidFields        — remove values now invalid under the new config
 *   5. dispatch(syncFromURL)     — commit clean state → userSelection
 *   6. dispatch(updateScreenConfig) — propagate any derived config changes
 *   7. dispatch(clearPendingSelection) — reset ephemeral slice to {}
 */

import {
  createListenerMiddleware,
  type TypedStartListening,
} from "@reduxjs/toolkit";

import type { AppDispatch, RootState } from "@/store/store";
import { setBootstrapDataStatus } from "@/store/slices/bootstrapDataStatus/bootstrapDataStatusSlice";
import type { UserSelectionState } from "@/store/slices/userSelection/userSelection";
import { syncFromURL } from "@/store/slices/userSelection/userSelection";
import type { CONFIG_SCHEMA } from "@/store/slices/screenConfig/screenConfigSlice";
import { setScreenConfig } from "@/store/slices/screenConfig/screenConfigSlice";
import {
  resolveModuleConfig,
  type ScreenTypeName,
} from "@/store/slices/screenConfig/resolveModuleConfig";
import { MODULE_CONFIG_REGISTRY } from "@/store/slices/screenConfig/moduleConfigRegistry";
import {
  filterBrandIdsByCategory,
  resolveActiveCategories,
  resolveSelectedCategoryId,
} from "@/utils/aiVisibility/categories";
import type { AiCategoryDefinition } from "@/store/slices/categories/categoriesSlice";
import {
  MARKETS_BASE_OVERRIDES,
  TRENDS_METRIC_BASE_OVERRIDES,
} from "@/store/slices/screenConfig/screenConfig.constants";
import type { PendingSelectionState } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import type { ScreenLevelSelectionState } from "@/store/slices/screenLevelSelection/screenLevelSelection";
import {
  clearPendingAPIChange,
  clearPendingSelection,
  setPendingSelection,
} from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { ALL_URL_KEYS, QO, QR, QU } from "@/constant/url";
import { isViewsV2Enabled } from "@utils/viewDefinition/flag";
import type { QuestionPickType } from "@utils/viewDefinition/types";
import { selectQuestionsAdapter } from "@/store/slices/questionsCatalog/questionsCatalogSelectors";
import {
  AGE_END,
  AGE_START,
  BRANDS,
  FILTER_PARAM,
  FILTERS,
  FILTERS_VERSION_PARAM,
  FILTERS_VERSION_V2,
  MARKETS,
  QUESTION_OPTIONS,
  SECONDARY_BRANDS,
  SEGMENTS,
  HUMAN_METRIC,
  MODEL,
  MODELS,
  STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION,
} from "@/constant/constantsThrougoutProject";
import { navigateTo } from "@/utils/navigationService";
import { convertPeriodForGranularity } from "@/utils/periodGranularity";
import { isAvailableInSelectedMarkets } from "@utils/markets/availability";
import { describeMarketPrune } from "./marketPruneNotice";
import ShowToaster from "@/toaster";
import {
  isOptionSelection,
  isRangeSelection,
  type FilterSelection,
} from "@interfaces/filters/domain";
import { dequal } from "dequal";
import {
  getMetricEntry,
  getModuleMetrics,
  getModuleScoreTypeOptions,
  moduleHasMultipleMethods,
  getModuleDefaultScoreType,
  getResponses,
  getDefaultResponse,
  getModuleResponses,
  getModuleDefaultResponse,
} from "@/store/slices/metricsCatalog/catalogSelectors";
import {
  getCategoriesForModule,
  normaliseModuleToken,
} from "@/store/slices/distributionsCatalog/catalogSelectors";
import type { MetricCatalogResponse } from "@/interfaces/v2Analytics";
import type { QuestionPickOption } from "@utils/viewDefinition/types";

// ─── Supporting types ─────────────────────────────────────────────────────────

/** Filter catalog entry carrying only what market pruning and the prune toast need. */
export interface SnapshotFilterEntry {
  name: string;
  kind: "OPTION" | "RANGE";
  markets: number[];
  optionMarkets: Record<string, number[]>;
}

/**
 * Snapshot of available entity IDs derived from the bootstrapped active-group
 * data in the Redux store. Passed to `pruneInvalidFields` so that function
 * remains pure (no store access inside it).
 */
export interface ActiveGroupSnapshot {
  /** All brand IDs currently loaded in the active group. */
  allBrandIds: (string | number)[];
  /**
   * Brand IDs narrowed to only those available in the currently selected
   * market. Equals `allBrandIds` when `config.isBrandsFilteredByMarket` is
   * false or no market is selected.
   */
  marketFilteredBrandIds: (string | number)[];
  /** All market IDs currently loaded in the active group. */
  allMarketIds: number[];
  /**
   * Market IDs narrowed to only those present in the currently selected
   * brand's `marketIds` array. Equals `allMarketIds` when
   * `config.isMarketsFilteredByBrand` is false or no brand is selected.
   */
  brandFilteredMarketIds: number[];
  /** All segment codes currently loaded in the active group. */
  allSegmentIds: string[];
  /** Segment codes that are marked `isApplied === true` (server-owned "applied" state). */
  activeSegmentIds: string[];
  /**
   * Filter catalog keyed by filter `code`, carrying only what market pruning
   * and the prune toast need. Empty record ⇒ catalog not loaded ⇒ market
   * pruning of `filters` is skipped (same guard style as `allQuestionKeys`).
   */
  filterCatalogByCode: Record<string, SnapshotFilterEntry>;
  /**
   * Segment catalog keyed by segment `code`. SYSTEM segments are never
   * market-pruned (their empty `markets` means "all markets" by contract).
   * Empty record ⇒ catalog not loaded ⇒ market pruning of `segments` skipped.
   */
  segmentCatalogByCode: Record<
    string,
    { label: string; type: string; markets: number[]; accessible: boolean }
  >;
  /**
   * Market display names keyed by market id, for user-facing prune-notice
   * copy ("not available in UK"). Missing entries degrade the copy to a
   * generic market phrase — never an error.
   */
  marketNamesById: Record<number, string>;
  /**
   * All question keys currently loaded in the active group.
   *
   * Populated from `selectQuestionsAdapter(state).ids` — the bootstrap
   * questions catalog transformer selector, market-filtered against
   * `state.userSelection.markets`.
   *
   * Used by `pruneInvalidFields` to validate `questionOptions` on the
   * Exclusive Questions page. The `pruneInvalidFields` questionOptions block
   * already guards against an empty store with `allQuestionKeys.length > 0`,
   * so pruning is safely skipped while the catalog is empty (e.g. before
   * bootstrap resolves).
   */
  allQuestionKeys: string[];
  /**
   * Metric catalog for catalog-driven scoreType/response pruning; null before
   * bootstrap → prune skipped.
   */
  metricsCatalog: MetricCatalogResponse | null;
  categories: AiCategoryDefinition[];
  /** Valid AI-model ids for `models` pruning — sourced from `activeGroup.aiModels`. */
  aiModelIds: number[];
  /**
   * Valid human-metric keys for `humanMetrics` pruning — sourced from
   * `activeGroup.humanMetrics` (conversion + equity catalog, plus fallback).
   */
  humanMetricKeys: string[];
}

// ─── Merge ─────────────────────────────────────────────────────────────────────

/**
 * Produce the effective selection by merging pending changes onto the current
 * committed selection using a three-tier priority scheme:
 *
 * Priority (highest → lowest):
 *   1. `pending`          — explicit in-flight user / navigation changes
 *   2. `screenLevelCtx`   — per-screen persisted context (brands / markets /
 *                           segments / secondaryBrands keyed by context key)
 *   3. `current`          — current committed `userSelection` baseline
 *
 * @pure
 * @example
 * mergeSelections({ mode: "TREND", brands: [1] }, { brands: [2, 3] })
 * // → { mode: "TREND", brands: [2, 3] }
 *
 * @example – with screen-level context
 * mergeSelections(
 *   { mode: "TREND", brands: [1] },
 *   { mode: "POINT_IN_TIME" },
 *   { brands: [5, 6] },
 * )
 * // → { mode: "POINT_IN_TIME", brands: [5, 6] }
 * // pending.mode wins; screenLevelCtx.brands wins over current.brands but
 * // would itself be overridden if pending also contained a `brands` field.
 */
export function mergeSelections(
  current: UserSelectionState,
  pending: PendingSelectionState,
  screenLevelCtx?: ScreenLevelSelectionState[string],
): UserSelectionState {
  // Strip the readiness meta-flags before merging into userSelection so that
  // `filtersReady`, `segmentsReady`, and `questionsReady` never leak into the
  // committed state.
  const {
    filtersReady: _fr,
    segmentsReady: _sr,
    questionsReady: _qr,
    ...selectionPart
  } = pending as PendingSelectionState & {
    filtersReady?: boolean;
    segmentsReady?: boolean;
    questionsReady?: boolean;
  };
  return { ...current, ...(screenLevelCtx ?? {}), ...selectionPart };
}

// ─── Resolve config ───────────────────────────────────────────────────────────

/**
 * Derive the screenConfig store key from a module name and screen type.
 *
 * Convention: `"${module}/${screenType}"` — e.g. `"equity/byBrand"`.
 *
 * @pure
 */
export function buildScreenKey(module: string, screenType: string): string {
  if (!module || !screenType) return "";
  return `${module}/${screenType}`;
}

// ─── Build context key for screenLevelSelection ───────────────────────────────

/**
 * Slot prefix for modules with `screenLevelSelectionPerModule`
 * (e.g. "aivisibility"), or null when the module shares the global slots.
 */
function moduleContextPrefix(module?: string | null): string | null {
  // Route / registry spellings differ ("aivisibility" vs "AI_VISIBILITY").
  const token = normaliseModuleToken(module).replace(/[-_]/g, "");
  if (!token) return null;
  const key = Object.keys(MODULE_CONFIG_REGISTRY).find(
    (k) => k.replace(/[-_]/g, "") === token,
  );
  return key &&
    MODULE_CONFIG_REGISTRY[key].config.screenLevelSelectionPerModule === true
    ? token.toLowerCase()
    : null;
}

/**
 * Compute the `screenLevelSelection` dictionary key for the given screen
 * context, using a three-tier priority scheme:
 *
 * 1. **Viz-specific** — when `vizType` is in
 *    `STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION` the key is the
 *    full compound string `${screenType}|${mode}|${vizType}`.
 *
 * 2. **Mode-specific** — when `mode` is in the list (e.g. `"PRE_POST"`) the
 *    key is just `mode`.  This keeps a single shared slot for that mode
 *    regardless of which screen the user is on.
 *
 * 3. **Screen-type default** — all other combinations key by `screenType`
 *    (e.g. `"brand"`, `"market"`, `"segment"`).
 *
 * Modules with `screenLevelSelectionPerModule` skip all three tiers and key by
 * `${module}|${screenType}` (e.g. `"aivisibility|brand"`), so their screens
 * neither share a slot with each other in PRE_POST nor with the same screen
 * type in other modules.
 *
 * @pure
 */
export function buildContextKey(
  screenType: string,
  mode: string,
  vizType: string,
  module?: string | null,
): string {
  const prefix = moduleContextPrefix(module);
  if (prefix) {
    return `${prefix}|${screenType}`;
  }
  if (
    STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION.includes(vizType)
  ) {
    return `${screenType}|${mode}|${vizType}`;
  }
  if (STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION.includes(mode)) {
    return mode;
  }
  return screenType;
}

// ─── Build URL from selection ─────────────────────────────────────────────────

type URLKeyType = (typeof ALL_URL_KEYS)[number];

const ARRAY_NUMERIC_URL_KEYS = new Set<string>([
  BRANDS,
  MARKETS,
  SEGMENTS,
  SECONDARY_BRANDS,
  HUMAN_METRIC,
  MODEL,
]);

const PARAM_FOR_PICK_TYPE: Record<QuestionPickType, string> = {
  RESPONSE: QO,
  SYSTEM_ROLLUP: QR,
  USER_ROLLUP: QU,
};

/**
 * Serialise `state` fields into a `URLSearchParams` using only the keys
 * declared in `supportedURLKeys` from the screen config.
 *
 * Special cases:
 * - `ageStart` / `ageEnd` are both written when iterating `ageStart`;
 *   `ageEnd` is skipped on its own iteration to avoid duplication.
 * - Array-numeric fields (brands, markets, segments, filters,
 *   secondaryBrands) use `append` so each ID becomes a separate param.
 * - `questionOptions` is `Record<string, QuestionPickOption[]>`. With Views
 *   V2 off, each pick is serialised as `"${questionKey}-${pick.id}"` and
 *   appended separately (the legacy grammar carries no `type`). With Views
 *   V2 on (`isViewsV2Enabled()`), picks are grouped per question in
 *   insertion order and fanned out into typed `qo`/`qu`/`qr` params — one
 *   param per (question, type) pair, value `"${questionKey}:${ids}"`.
 *
 * Reads `isViewsV2Enabled()` at call time, so this function is env-dependent
 * rather than pure for the `questionOptions` key; every other key is still
 * a pure function of `state`/`supportedURLKeys`.
 */
export function buildURLFromSelection(
  state: UserSelectionState,
  supportedURLKeys: URLKeyType[],
): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of supportedURLKeys) {
    if (key === AGE_END) continue; // written together with AGE_START

    if (key === FILTERS) {
      // v2 filters URL grammar: `fv=2` version marker, emitted once, plus one
      // `f=` param per FilterSelection —
      //   OPTION → f=<filterCode>:<optCode>,<optCode>,...
      //   RANGE  → f=<filterCode>:r=<min>-<max>
      // `state.filters` is typed `number[]` (transitional state type) but the
      // actual runtime value is `FilterSelection[]` — cast as the rest of the
      // codebase already does (see userSelection.ts's `UpdateFieldPayload`
      // comment).
      const filterSelections =
        (state.filters as unknown as FilterSelection[] | undefined) ?? [];
      if (filterSelections.length > 0) {
        params.set(FILTERS_VERSION_PARAM, FILTERS_VERSION_V2);
        filterSelections.forEach((selection) => {
          if (isOptionSelection(selection)) {
            params.append(
              FILTER_PARAM,
              `${selection.code}:${selection.options.join(",")}`,
            );
          } else if (isRangeSelection(selection)) {
            params.append(
              FILTER_PARAM,
              `${selection.code}:r=${selection.range.min}-${selection.range.max}`,
            );
          }
        });
      }
      continue;
    }

    if (key === AGE_START) {
      const ageRange = state.ageRange;
      if (Array.isArray(ageRange) && ageRange.length >= 2) {
        params.set(AGE_START, String(ageRange[0]));
        params.set(AGE_END, String(ageRange[1]));
      }
      continue;
    }

    if (ARRAY_NUMERIC_URL_KEYS.has(key)) {
      // Two fields' state names deliberately don't match their URL spelling:
      // - MODEL ("model"): URL key -> state field MODELS ("models").
      // - HUMAN_METRIC ("humanMetric"): URL key -> state field "humanMetrics"
      //   (plural, matches the backend's getHumanMetrics()).
      const stateKey =
        key === MODEL ? MODELS : key === HUMAN_METRIC ? "humanMetrics" : key;
      const values = (state as unknown as Record<string, unknown>)[
        stateKey
      ] as number[] | undefined;
      values?.forEach((v) => params.append(key, String(v)));
      continue;
    }

    if (key === QUESTION_OPTIONS) {
      const qOpts = (state as unknown as Record<string, unknown>)[key] as
        | Record<string, QuestionPickOption[]>
        | undefined;
      if (qOpts) {
        if (isViewsV2Enabled()) {
          // V2 grammar: one `qo`/`qu`/`qr` param per (question, type) pair,
          // grouped per question in insertion order; within a question, type
          // order follows first-occurrence order in that question's picks.
          Object.entries(qOpts).forEach(([questionKey, opts]) => {
            const idsByType = new Map<QuestionPickType, number[]>();
            opts.forEach((opt) => {
              const ids = idsByType.get(opt.type);
              if (ids) {
                ids.push(opt.id);
              } else {
                idsByType.set(opt.type, [opt.id]);
              }
            });
            idsByType.forEach((ids, type) => {
              params.append(
                PARAM_FOR_PICK_TYPE[type],
                `${questionKey}:${ids.join(",")}`,
              );
            });
          });
        } else {
          // Legacy grammar: bare `KEY-id` tokens. The pick's `type` is
          // dropped here — the typed `qo`/`qr`/`qu` params carry it instead.
          Object.entries(qOpts).forEach(([questionKey, opts]) => {
            opts.forEach((opt) =>
              params.append(key, `${questionKey}-${opt.id}`),
            );
          });
        }
      }
      continue;
    }

    const value = (state as unknown as Record<string, unknown>)[key];
    if (value != null && value !== "") {
      params.set(key, String(value));
    }
  }

  return params;
}

// ─── Build active-group snapshot ──────────────────────────────────────────────

/**
 * Extract the current set of available entity IDs from the Redux store.
 *
 * This is the ONLY place that reaches into the raw store shape; every
 * downstream pure function receives the structured `ActiveGroupSnapshot`
 * instead of the raw store.
 *
 * When `config.isBrandsFilteredByMarket` is true, the brand pool is narrowed
 * to those whose `marketIds` array includes the first selected market.
 */
export function buildActiveGroupSnapshot(
  state: RootState,
  effective: UserSelectionState,
  config: CONFIG_SCHEMA,
): ActiveGroupSnapshot {
  // Priority 1: isBrandsFilteredByReputationMarket (Reputation By Brand / By Segment)
  //   Filter brands whose `reputationMarketIds` overlaps with ANY selected market.
  //   Mirrors the eligibility check in useReputationPayloadOverride.ts exactly:
  //     brand.reputationMarketIds is a non-empty array AND
  //     at least one selected market id is present in that array.
  //   Falls back to allBrandIds when no markets are selected yet.
  //
  // Priority 2: isBrandsFilteredByOwnReputationMarkets (Reputation By Market)
  //   Filter brands whose `reputationMarketIds` overlaps with the brand's OWN `marketIds`.
  //   Used when there are no pre-selected markets to check against (By Market screen:
  //   brand is the primary dimension, markets are derived from the selected brand).
  //   A brand is eligible if it has reputation data for at least one of its own markets.
  //
  // Priority 3: isBrandsFilteredByMarket (generic market-scoped brand filter)
  //   Filter brands whose `marketIds` includes the first selected market.
  //   Falls back to allBrandIds when no market is selected yet.
  //
  // Priority 4: no filtering — return the full brand pool.

  // ── Markets ───────────────────────────────────────────────────────────────
  // marketsSlice state shape: { markets: EntityAdapterState, groups: EntityAdapterState }
  const marketsState = (state.activeGroup.markets as any).markets;
  const rawMarketIds: number[] = (marketsState?.ids ?? []) as number[];
  // Sort so markets whose `identity` starts with "US" come first;
  // the original position-based order is preserved within each group
  // (Array.prototype.sort is stable in all modern environments).
  const allMarketIds: number[] = [...rawMarketIds].sort((a, b) => {
    const aIdentity: string =
      (marketsState?.entities?.[a] as any)?.identity ?? "";
    const bIdentity: string =
      (marketsState?.entities?.[b] as any)?.identity ?? "";
    const aIsUS = aIdentity.toUpperCase().startsWith("US");
    const bIsUS = bIdentity.toUpperCase().startsWith("US");
    if (aIsUS && !bIsUS) return -1;
    if (!aIsUS && bIsUS) return 1;
    return 0;
  }).filter((id) => {
    const market = marketsState?.entities?.[id] as
      | { identity?: string }
      | undefined;
    const identityPrefixes = config.supportedMarketIdentities;
    if (!identityPrefixes?.length) return true;
    return identityPrefixes.some((prefix) =>
      String(market?.identity ?? "")
        .toUpperCase()
        .startsWith(prefix.toUpperCase()),
    );
  });
  // No silent fallback to an arbitrary market when `supportedMarketIdentities`
  // excludes every market the account has (e.g. AI Visibility's "US" filter
  // on a non-US account). `allMarketIds` staying empty is the correct signal
  // — it flows into an empty `markets[]` selection, and `useV2Payload`'s
  // empty-markets guard renders "no data" instead of fetching a market that
  // violates the restriction the (disabled) Markets dropdown can't surface.

  // ── Brands ────────────────────────────────────────────────────────────────
  const allBrandIds = state.activeGroup.brands.ids as number[];

  const selectedMarketIds = (
    effective.markets?.length > 0 ? effective?.markets : allMarketIds
  ) as number[];

  let marketFilteredBrandIds: (string | number)[] =
    config.isBrandsFilteredByReputationMarket && selectedMarketIds.length > 0
      ? allBrandIds.filter((id) => {
          const brand = state.activeGroup.brands.entities[id] as any;
          const repMarketIds: number[] | undefined = brand?.reputationMarketIds;
          return (
            Array.isArray(repMarketIds) &&
            repMarketIds.length > 0 &&
            selectedMarketIds.some((mid) => repMarketIds.includes(mid))
          );
        })
      : config.isBrandsFilteredByOwnReputationMarkets
        ? allBrandIds.filter((id) => {
            const brand = state.activeGroup.brands.entities[id] as any;
            const repMarketIds: number[] | undefined =
              brand?.reputationMarketIds;
            const brandMarketIds: number[] | undefined = brand?.marketIds;
            return (
              Array.isArray(repMarketIds) &&
              repMarketIds.length > 0 &&
              Array.isArray(brandMarketIds) &&
              brandMarketIds.some((mid) => repMarketIds.includes(mid))
            );
          })
        : config.isBrandsFilteredByMarket && selectedMarketIds?.length > 0
          ? allBrandIds.filter((id) =>
              (
                (state.activeGroup.brands.entities[id] as any)?.marketIds ?? []
              ).includes(selectedMarketIds[0]),
            )
          : allBrandIds;

  const categories: AiCategoryDefinition[] = resolveActiveCategories(
    state.activeGroup.categories?.categories ?? [],
  );
  if (config.isBrandsFilteredByCategory) {
    const selectedCategoryId = resolveSelectedCategoryId(
      categories,
      effective.category,
      config.defaultCategory,
    );
    marketFilteredBrandIds = filterBrandIdsByCategory(
      marketFilteredBrandIds,
      state.activeGroup.brands.entities,
      categories.find((category) => category.id === selectedCategoryId),
    );
  }

  // When isMarketsFilteredByBrand is true, narrow the market pool to only
  // those whose IDs appear in the selected brand's `marketIds` array.
  // Brand entities carry a `marketIds: number[]` field, so we invert the
  // brand→market relationship here rather than relying on the Market entity.
  const selectedBrandId = effective.brands?.[0];
  const brandFilteredMarketIds =
    config.isMarketsFilteredByBrand && selectedBrandId != null
      ? allMarketIds.filter((id) =>
          (
            (state.activeGroup.brands.entities[selectedBrandId] as any)
              ?.marketIds ?? []
          ).includes(id),
        )
      : allMarketIds;

  // ── Segments ──────────────────────────────────────────────────────────────
  // segmentsSlice state shape: { segments: EntityAdapterState, categories: EntityAdapterState, ... }
  // Segment shape (src/interfaces/segments/domain.ts) is keyed by
  // `code: string` and carries a server-owned `isApplied: boolean` flag —
  // there is no `isActive` / `segmentMarkets` field on this entity anymore.
  const segmentsState = (state.activeGroup.segments as any).segments;
  const allSegmentIds: string[] = (segmentsState?.ids ?? []) as string[];
  const activeSegmentIds: string[] = allSegmentIds.filter(
    (id) => segmentsState?.entities?.[id]?.isApplied === true,
  );

  // ── Filter / segment catalogs for market pruning ──────────────────────────
  const filtersEntities = (state.activeGroup.filters.filters?.entities ??
    {}) as Record<
    string,
    | {
        code: string;
        name: string;
        displayLabel: string;
        kind: "OPTION" | "RANGE";
        markets: number[];
        options?: { code: string; markets: number[] }[];
      }
    | undefined
  >;
  const filterCatalogByCode: Record<string, SnapshotFilterEntry> = {};
  for (const key of Object.keys(filtersEntities)) {
    const f = filtersEntities[key];
    if (!f) continue;
    const optionMarkets: Record<string, number[]> = {};
    for (const o of f.options ?? []) optionMarkets[o.code] = o.markets;
    filterCatalogByCode[f.code] = {
      name: f.displayLabel || f.name,
      kind: f.kind,
      markets: f.markets,
      optionMarkets,
    };
  }

  const segmentCatalogByCode: Record<
    string,
    { label: string; type: string; markets: number[]; accessible: boolean }
  > = {};
  for (const id of allSegmentIds) {
    const s = segmentsState?.entities?.[id];
    if (!s) continue;
    segmentCatalogByCode[s.code] = {
      label: s.label,
      type: s.type,
      markets: s.markets,
      accessible: s.accessible,
    };
  }

  const marketNamesById: Record<number, string> = {};
  for (const id of rawMarketIds) {
    const m = marketsState?.entities?.[id] as
      | { name?: string; identity?: string }
      | undefined;
    const name = m?.name || m?.identity;
    if (name) marketNamesById[id] = name;
  }

  // ── Questions ─────────────────────────────────────────────────────────────
  // Bootstrap questions catalog, transformed into the legacy entity-adapter
  // shape and market-filtered by `selectQuestionsAdapter`.
  const allQuestionKeys: string[] = selectQuestionsAdapter(state).ids;

  // ── Metric catalog ────────────────────────────────────────────────────────
  const metricsCatalog = state.activeGroup.metricsCatalog.catalog;

  // ── AI models ─────────────────────────────────────────────────────────────
  // Optional-chained: test stores (and any partially preloaded state) may not
  // register the aiModels slice — treat that as "no models loaded yet".
  const aiModelIds: number[] = state.activeGroup.aiModels?.ids ?? [];
  const humanMetricsState = state.activeGroup.humanMetrics;
  const humanMetricMarketIds = effective.markets ?? [];
  const humanMetricKeys: string[] = (humanMetricsState?.ids ?? []).filter(
    (key) => {
      const metric = humanMetricsState?.entities?.[key];
      if (!metric) return false;
      if (
        humanMetricMarketIds.length === 0 ||
        (metric.marketIds?.length ?? 0) === 0
      ) {
        return true;
      }
      return (metric.marketIds ?? []).some((id) =>
        humanMetricMarketIds.includes(id),
      );
    },
  );

  return {
    allBrandIds,
    marketFilteredBrandIds,
    allMarketIds,
    brandFilteredMarketIds,
    allSegmentIds,
    activeSegmentIds,
    filterCatalogByCode,
    segmentCatalogByCode,
    marketNamesById,
    allQuestionKeys,
    metricsCatalog,
    categories,
    aiModelIds,
    humanMetricKeys,
  };
}

// ─── Prune invalid fields ────────────────────────────────────────────────────

/**
 * Return a copy of `effective` with every field that is now invalid under
 * `config` either removed, clamped, or reset to its declared default.
 *
 * Prune catalogue:
 * ─────────────────────────────────────────────────────────────────────────────
 * `markets`           → keep only IDs present in `allMarketIds`; cap at `maximumAllowedMarkets`;
 *                       when the result is empty, default to the first `maximumAllowedMarkets`
 *                       IDs from `allMarketIds`; if still below `minimumRequiredMarkets`, top up
 *                       with additional IDs from the pool until the minimum is met
 * `brands`            → keep only IDs present in `marketFilteredBrandIds`; cap at `maximumAllowedBrands`;
 *                       when the result is empty, default to the first `maximumAllowedBrands`
 *                       IDs from `marketFilteredBrandIds`; if still below `minimumRequiredBrands`,
 *                       top up with additional IDs from the pool until the minimum is met
 * `secondaryBrands`   → keep only IDs in `marketFilteredBrandIds` ∖ primaryBrands; cap at max
 * `segments`          → keep only valid IDs; zero is accepted (screen-switch handled upstream)
 * `segments`          → additionally: USER segments must be applicable in every
 *                       selected market (SYSTEM bypass); runs after SEGMENT_ONLY seeding
 * `filters`           → selections kept only when filter + picked options are
 *                       applicable in every selected market; option picks narrowed
 * `mode`              → reset to `defaultMode` when not in `supportedModes`
 * `visualizationType` → reset to `defaultVisualisationType` when not in `supportedVisualisationTypes`
 * `base`              → reset to `defaultBase` when not in `supportedBases`
 * `period`            → reset to `defaultPeriod` when not in `supportedPeriod`
 * `trendsMetric`      → snapped to a metric the catalog offers for this module + markets
 * `scoreType`         → snapped to a catalog method for the selected metric (TREND) or the
 *                       module union (other modes); defaults to the metric/module default
 * `response`          → snapped to a response of the resolved method; null when that method
 *                       carries no response menu
 *
 * @pure
 */

/**
 * Market-applicability gate for applied selections, shared by
 * `pruneInvalidFields` and the listener's post-hoc market re-prune: the
 * `isMarketsFilteredByBrand` brand-reseed path reassigns `cleanState.markets`
 * AFTER `pruneInvalidFields` returns, so both call sites must gate against
 * the market list actually being committed.
 *
 * - USER segments and filters/options must be applicable in EVERY market id
 *   in `marketIds`; SYSTEM segments always pass (their empty `markets` means
 *   "all markets" by contract).
 * - An OPTION selection whose picked options all fail is dropped; RANGE
 *   selections gate on the filter alone.
 * - An empty catalog record ⇒ that input is returned as-is (bootstrap
 *   pending — same guard style as `allQuestionKeys`).
 *
 * @pure
 */
export function pruneSelectionsForMarketApplicability(
  filters: FilterSelection[],
  segments: string[],
  marketIds: number[],
  snapshot: Pick<
    ActiveGroupSnapshot,
    "filterCatalogByCode" | "segmentCatalogByCode"
  >,
): { filters: FilterSelection[]; segments: string[] } {
  let outSegments = segments;
  if (Object.keys(snapshot.segmentCatalogByCode).length > 0) {
    outSegments = segments.filter((code) => {
      const entry = snapshot.segmentCatalogByCode[code];
      if (!entry) return false;
      if (!entry.accessible) return false;
      if (entry.type === "SYSTEM") return true;
      return isAvailableInSelectedMarkets(entry.markets, marketIds);
    });
  }

  let outFilters = filters;
  if (Object.keys(snapshot.filterCatalogByCode).length > 0) {
    outFilters = filters
      .map((selection) => {
        const entry = snapshot.filterCatalogByCode[selection.code];
        if (!entry) return null;
        if (!isAvailableInSelectedMarkets(entry.markets, marketIds))
          return null;
        if (selection.kind === "OPTION") {
          const options = selection.options.filter((code) =>
            isAvailableInSelectedMarkets(entry.optionMarkets[code], marketIds),
          );
          if (options.length === 0) return null;
          if (options.length !== selection.options.length)
            return { ...selection, options };
        }
        return selection;
      })
      .filter((s): s is FilterSelection => s !== null);
  }

  return { filters: outFilters, segments: outSegments };
}

export function pruneInvalidFields(
  effective: UserSelectionState,
  config: CONFIG_SCHEMA,
  snapshot: ActiveGroupSnapshot,
): UserSelectionState {
  const out: UserSelectionState = { ...effective };
  // ── markets ───────────────────────────────────────────────────────────────
  // Use `brandFilteredMarketIds` as the valid pool — it equals `allMarketIds`
  // when `config.isMarketsFilteredByBrand` is false, so this is safe in all
  // cases.  When the flag is true the pool is narrowed to only the markets
  // present in the selected brand's `marketIds` array.
  {
    const marketPool = snapshot.brandFilteredMarketIds;
    // Filter by iterating the pool (not out.markets) so the result preserves
    // the canonical snapshot order (US-first, then others) rather than the
    // user-selection order stored in the URL.
    const selectedMarketSet = new Set(out.markets);
    let markets = marketPool.filter((id) => selectedMarketSet.has(id));
    if (config.maximumAllowedMarkets != null) {
      markets = markets.slice(0, config.maximumAllowedMarkets);
    }
    // When no markets survive pruning (e.g. none were in the URL yet), seed
    // the selection with the first `maximumAllowedMarkets` available markets.
    if (markets.length === 0 && marketPool.length > 0) {
      const limit = config.maximumAllowedMarkets ?? marketPool.length;
      markets = marketPool.slice(0, limit);
    }
    // Top up to satisfy minimumRequiredMarkets when pruning left fewer items
    // than the declared minimum (e.g. URL had one valid market but the screen
    // requires at least two). Additional IDs are drawn from the pool in order,
    // skipping any already selected.
    if (
      config.minimumRequiredMarkets != null &&
      markets.length < config.minimumRequiredMarkets &&
      marketPool.length > 0
    ) {
      const needed = config.minimumRequiredMarkets - markets.length;
      const existingSet = new Set(markets);
      const candidates = marketPool.filter((id) => !existingSet.has(id));
      markets = [...markets, ...candidates.slice(0, needed)];
    }
    out.markets = markets;
  }

  // ── AI category ───────────────────────────────────────────────────────────
  if (config.supportCategories) {
    out.category = resolveSelectedCategoryId(
      snapshot.categories,
      out.category,
      config.defaultCategory,
    );
  }

  // ── brands ────────────────────────────────────────────────────────────────
  {
    let brands = out.brands.filter((id) =>
      snapshot.marketFilteredBrandIds
        ?.map((ele) => String(ele))
        .includes(String(id)),
    );
    if (config.maximumAllowedBrands != null) {
      brands = brands.slice(0, config.maximumAllowedBrands);
    }
    // When no brands survive pruning (e.g. none were in the URL yet), seed
    // the selection with the first `maximumAllowedBrands` available brands.
    if (brands.length === 0 && snapshot.marketFilteredBrandIds.length > 0) {
      const limit =
        config.maximumAllowedBrands ?? snapshot.marketFilteredBrandIds.length;
      brands = snapshot.marketFilteredBrandIds.slice(0, limit);
    }
    // Top up to satisfy minimumRequiredBrands when pruning left fewer items
    // than the declared minimum (e.g. URL had one valid brand but the screen
    // requires at least three for PERCEPTUAL). Additional IDs are drawn from
    // the market-filtered brand pool in order, skipping already-selected IDs.
    if (
      config.minimumRequiredBrands != null &&
      brands.length < config.minimumRequiredBrands &&
      snapshot.marketFilteredBrandIds.length > 0
    ) {
      const needed = config.minimumRequiredBrands - brands.length;
      const existingSet = new Set(brands);
      const candidates = snapshot.marketFilteredBrandIds.filter(
        (id) => !existingSet.has(id),
      );
      brands = [...brands, ...candidates.slice(0, needed)];
    }
    out.brands = brands;
  }

  // ── secondaryBrands ───────────────────────────────────────────────────────
  {
    // primarySet uses string keys for safe cross-type comparison.
    const primarySet = new Set(out.brands.map((id) => String(id)));

    let secondary = out.secondaryBrands.filter(
      (id) =>
        snapshot.marketFilteredBrandIds
          ?.map((e) => String(e))
          .includes(String(id)) && !primarySet.has(String(id)),
    );
    if (config.maximumAllowedSecondaryBrands != null) {
      secondary = secondary.slice(0, config.maximumAllowedSecondaryBrands);
    }
    // Top up to satisfy minimumRequiredSecondaryBrands when pruning left fewer
    // items than the declared minimum. Candidates are drawn from the
    // market-filtered brand pool, explicitly excluding:
    //   • IDs already in primary brands (`primarySet`) — secondary brands must
    //     never overlap with the primary selection.
    //   • IDs already selected as secondary brands (`existingSet`) — no duplicates.
    if (
      config.minimumRequiredSecondaryBrands != null &&
      secondary.length < config.minimumRequiredSecondaryBrands &&
      snapshot.marketFilteredBrandIds.length > 0
    ) {
      const needed = config.minimumRequiredSecondaryBrands - secondary.length;
      const existingSet = new Set(secondary.map((id) => String(id)));
      const candidates = snapshot.marketFilteredBrandIds.filter(
        (id) => !primarySet.has(String(id)) && !existingSet.has(String(id)),
      );
      secondary = [...secondary, ...candidates.slice(0, needed)];
    }
    // Seed when empty: when secondaryBrands is still empty after pruning and
    // the screen supports secondary brands, auto-select the first N candidates
    // from the market-filtered brand pool (excluding primary brands).
    //   • BUTTERFLY          → maximumAllowedSecondaryBrands = 1  → seed 1
    //   • DIFFERENTIATION    → minimumRequiredSecondaryBrands = 2 → already seeded above
    //   • other screens with supportSecondaryBrands = true but no min/max
    //                        → fall back to 2 (mirrors previous component behaviour)
    // This replaces the `useEffect` that lived in SecondaryBrandsDropdownView.
    if (
      secondary.length === 0 &&
      config.supportSecondaryBrands === true &&
      snapshot.marketFilteredBrandIds.length > 0
    ) {
      const limit =
        config.maximumAllowedSecondaryBrands ??
        config.minimumRequiredSecondaryBrands ??
        2;
      const candidates = snapshot.marketFilteredBrandIds.filter(
        (id) => !primarySet.has(String(id)),
      );
      secondary = candidates.slice(0, limit);
    }
    out.secondaryBrands = secondary;
  }

  // ── segments ──────────────────────────────────────────────────────────────
  {
    const modalMode = config.segmentFilterModalMode;

    if (modalMode === "SEGMENT_ONLY") {
      // `filters` is not in `supportedURLKeys` for SEGMENT_ONLY screens.
      // Prefer any segments the user already has selected that are still valid
      // in the loaded segment pool. Only fall back to the server-owned
      // `activeSegmentIds` when none of the carried-over segments survive
      // pruning (e.g. first visit, screen-switch reset, or URL junk).
      const validFromEffective = out.segments.filter((id) =>
        snapshot.allSegmentIds.includes(id),
      );
      out.segments =
        validFromEffective.length > 0
          ? validFromEffective
          : [...snapshot.activeSegmentIds];
    } else if (modalMode === "SEGMENT_WITH_FILTERS") {
      // The user has full control; zero segments is a valid state.
      // Only prune IDs that are no longer present in the loaded segment pool —
      // never force a fallback to activeSegmentIds.
      out.segments = out.segments.filter((id) =>
        snapshot.allSegmentIds.includes(id),
      );
    } else {
      // Default: prune-only — keep valid IDs; zero segments is accepted.
      // The screen-switch reset upstream already clears segments, so there
      // is no need to fall back to activeSegmentIds here.
      out.segments = out.segments.filter((id) =>
        snapshot.allSegmentIds.includes(id),
      );
    }
  }

  // ── market applicability (filters + segments) ─────────────────────────────
  // Runs after SEGMENT_ONLY seeding so seeding cannot reintroduce an
  // inapplicable segment.
  {
    const gated = pruneSelectionsForMarketApplicability(
        out.filters as unknown as FilterSelection[],
        out.segments,
        out.markets,
        snapshot,
      );
    if (config.apiGroupedBy === "SEGMENT") {
      out.filters = [] as unknown as typeof out.filters;
      out.segments = gated.segments;
    } else {
      out.filters = gated.filters as unknown as typeof out.filters;
      out.segments = gated.segments;
    }
  }

  // ── mode ──────────────────────────────────────────────────────────────────
  {
    const supported = config.supportedModes;
    if (
      supported != null &&
      supported.length > 0 &&
      !supported.includes(out.mode as any)
    ) {
      out.mode = (config.defaultMode ??
        "POINT_IN_TIME") as UserSelectionState["mode"];
    }
  }

  // ── visualizationType ─────────────────────────────────────────────────────
  // Correct when:
  //   (a) visualizationType is empty / not yet set, OR
  //   (b) visualizationType is set but not in the supported list.
  //   (c) supportVisualisationSwitch is false — the screen owns a fixed
  //       visualisation type and the user may not deviate from it.  If the
  //       carried-over value differs from defaultVisualisationType, reset it.
  // Previously only case (b) was guarded (`out.visualizationType &&` skipped
  // empty values), which left "" in the URL and caused PageHeader's useEffect
  // to fire a redundant second setPendingSelection dispatch on module switch.
  {
    const supported = config.supportedVisualisationTypes;
    if (supported != null && supported.length > 0) {
      if (
        !out.visualizationType ||
        !supported.includes(out.visualizationType as any)
      ) {
        out.visualizationType =
          (config.defaultVisualisationType as string) ?? "";
      }
    }
    // Case (c): when switching is disabled, always lock to the declared default.
    if (
      config.supportVisualisationSwitch === false &&
      config.defaultVisualisationType != null &&
      out.visualizationType !== config.defaultVisualisationType
    ) {
      out.visualizationType = config.defaultVisualisationType as string;
    }
  }

  // ── base ──────────────────────────────────────────────────────────────────
  {
    const supported = config.supportedBases;
    if (
      supported != null &&
      supported.length > 0 &&
      !supported.includes(out.base as any)
    ) {
      out.base = (config.defaultBase ?? supported[0] ?? "GENPOP") as string;
    }
  }
  // ── period ────────────────────────────────────────────────────────────────
  {
    const supported = config.supportedPeriod;
    if (
      supported != null &&
      supported.length > 0 &&
      !supported.includes(out.period as any)
    ) {
      out.period = (config.defaultPeriod ?? supported[0] ?? "MV_12") as string;
    }
  }

  // ── trendsMetric ───────────────────────────────────────────────────────────
  // The metrics catalog is the single source of truth for which metrics exist
  // on this module for the currently selected markets, so a carried-over
  // trendsMetric from another screen (or URL junk) is snapped here — components
  // never self-correct the selection during render.
  //
  // The catalog is a hard bootstrap dependency: while it is still null (pre-
  // bootstrap) the metric axis passes through untouched, and a later prune
  // cycle re-runs once the catalog arrives.
  {
    if (snapshot.metricsCatalog != null) {
      if (config.supportTrendsMetric) {
        const metrics = getModuleMetrics(
          snapshot.metricsCatalog,
          (config.module as string) ?? "",
          out.markets ?? [],
        );
        const valid = metrics.map((m) => m.metricKey);
        if (
          valid.length > 0 &&
          (out.trendsMetric == null || !valid.includes(out.trendsMetric))
        ) {
          out.trendsMetric =
            config.defaultTrendsMetric != null &&
            valid.includes(config.defaultTrendsMetric)
              ? config.defaultTrendsMetric
              : valid[0];
        } else if (
          valid.length === 0 &&
          (config.skipMetricCatalog || (config.apiMetricKeys?.length ?? 0) > 0)
        ) {
          const fallback = config.apiMetricKeys ?? [];
          if (
            out.trendsMetric == null ||
            (fallback.length > 0 && !fallback.includes(out.trendsMetric))
          ) {
            out.trendsMetric =
              config.defaultTrendsMetric ?? fallback[0] ?? out.trendsMetric;
          }
        }
      }
    } else if (
      config.supportTrendsMetric &&
      (config.skipMetricCatalog || (config.apiMetricKeys?.length ?? 0) > 0)
    ) {
      const fallback = config.apiMetricKeys ?? [];
      if (
        out.trendsMetric == null ||
        (fallback.length > 0 && !fallback.includes(out.trendsMetric))
      ) {
        out.trendsMetric =
          config.defaultTrendsMetric ?? fallback[0] ?? out.trendsMetric;
      }
    }
  }

  // ── human metrics ─────────────────────────────────────────────────────────
  if (!config.supportHumanMetrics) {
    out.humanMetrics = [];
  } else {
    // A stray "humanMetrics" (plural) URL param falls through parseURLParams'
    // generic single-value branch as a raw string, not the array this field
    // is typed as -- `out.humanMetrics?.length` alone doesn't catch that
    // (strings have `.length` too), so guard with Array.isArray before the
    // `.filter()` below crashes on it.
    if (!Array.isArray(out.humanMetrics)) {
      out.humanMetrics = out.humanMetrics ? [out.humanMetrics as unknown as string] : [];
    }
    const valid = snapshot.humanMetricKeys ?? [];
    if (valid.length > 0 && out.humanMetrics?.length) {
      out.humanMetrics = out.humanMetrics.filter((key) => valid.includes(key));
    }
    // A multi-pick carried over from BAR/TABLE onto a capped viz (GAUGE → 1)
    // must be trimmed, or the dropdown shows "N selected" in single-select
    // mode and the payload still requests every metric.
    if (config.maximumAllowedHumanMetrics != null) {
      out.humanMetrics = out.humanMetrics.slice(0, config.maximumAllowedHumanMetrics);
    }
    const isEmpty = !out.humanMetrics || out.humanMetrics.length === 0;
    if (isEmpty && config.supportHumanMetricsToggle === false) {
      const defaults = config.defaultHumanMetrics ?? [];
      const defaultInCatalog =
        valid.length === 0
          ? defaults
          : defaults.filter((key) => valid.includes(key));
      if (defaultInCatalog.length > 0) {
        out.humanMetrics = [...defaultInCatalog];
      } else if (valid.length > 0) {
        out.humanMetrics = [valid[0]];
      } else {
        out.humanMetrics = [...defaults];
      }
    }
  }

  // ── AI model(s) (By-Model PRE_POST scalar / POINT_IN_TIME multi-select /
  //    By-Brand model filter) ────────────────────────────────────────────
  if (config.apiGroupedBy === "MODEL" && config.apiViewMode === "PRE_POST") {
    // By-Model PRE_POST shows one model's full metric set at a time
    // (AiModelDropdown) — without a valid selection the request has nothing
    // to scope to, so an invalid/missing pick snaps to the first model
    // rather than left dangling.
    const validAiModelIds = snapshot.aiModelIds;
    const current = out.models?.[0];
    if (current != null && validAiModelIds.includes(current)) {
      out.models = [current];
    } else {
      out.models = validAiModelIds.length > 0 ? [validAiModelIds[0]] : [];
    }
  } else if (config.apiGroupedBy === "MODEL" && config.aiModelIsMultiSelect) {
    // Narrows which model cards render for By-Model POINT_IN_TIME / TREND.
    // Invalid ids are dropped, and an empty/fully-invalid selection falls
    // back to every available model — "all selected" is the default until
    // the user narrows it, and at least one model always stays selected
    // (enforced here as well as by the dropdown's minimum-selection rule).
    const validAiModelIds = snapshot.aiModelIds;
    const filtered = (out.models ?? []).filter((id) =>
      validAiModelIds.includes(id),
    );
    out.models = filtered.length > 0 ? filtered : [...validAiModelIds];
  } else if (config.supportAiModel) {
    // By-Brand's model filter is optional — a single valid pick narrows to
    // just that model; anything else (empty, invalid, or already "every
    // model") resets to every available id, which AiModelDropdownView reads
    // back as "All Models" rather than an arbitrary specific pick.
    const validAiModelIds = snapshot.aiModelIds;
    const current = out.models?.length === 1 ? out.models[0] : undefined;
    out.models =
      current != null && validAiModelIds.includes(current)
        ? [current]
        : [...validAiModelIds];
  } else {
    out.models = [];
  }

  // ── scoreType (method axis) + response (slice axis) ────────────────────────
  // Two-axis scoring: `scoreType` is the calculation method (PERCENTAGE / MEAN /
  // LEGACY …) and `response` is the slice within that method (POSITIVE, NET,
  // bands …), which is null whenever the resolved method carries no response
  // menu. Both are validated against the catalog here, in this order, because
  // the valid response set depends on the resolved method.
  //
  // TREND validates against the SELECTED METRIC (a trend chart plots one metric,
  // so its own methods/bands apply); every other mode validates against the
  // MODULE union (the screen plots many metrics at once). Module-level selectors
  // are market-filtered, so a module reduces to whatever its market-eligible
  // metrics offer.
  //
  // While the catalog is still null (pre-bootstrap) both axes pass through
  // untouched — same lifetime as the trendsMetric guard above.
  //
  // DISTRIBUTION is exempt from both axes. A distribution already IS the response
  // breakdown — every band of the question is a segment of the bar — so there
  // is no method to pick and no slice to take within it. PERCENTAGE is the only
  // coherent method and every module offers it, so this snap can never leave a
  // module on an unsupported value. PageHeader hides the controls; this keeps
  // the stored axes honest, so the URL, saved views and the AI assistant
  // context cannot claim MEAN while a distribution is on screen.
  {
    if (out.visualizationType === "DISTRIBUTION") {
      out.scoreType = "PERCENTAGE";
      out.response = null;
    } else if (snapshot.metricsCatalog != null) {
      const module = (config.module as string) ?? "";
      const markets = out.markets ?? [];
      const metricEntry =
        out.mode === "TREND" && out.trendsMetric != null
          ? getMetricEntry(snapshot.metricsCatalog, module, out.trendsMetric)
          : undefined;
      const validMethods = metricEntry
        ? Object.keys(metricEntry.scoreTypes ?? {})
        : getModuleScoreTypeOptions(
            snapshot.metricsCatalog,
            module,
            markets,
          ).map((o) => o.key);
      if (validMethods.length > 0 && !validMethods.includes(out.scoreType)) {
        out.scoreType =
          metricEntry?.defaultScoreType ??
          getModuleDefaultScoreType(snapshot.metricsCatalog, module, markets) ??
          validMethods[0];
      }

      // ── response (slice within the resolved method) ────────────────────────
      // Product rule: only MULTI-METHOD modules (reputation today) carry a
      // module-level response in PIT/PRE_POST. Single-method modules show no
      // response control and send none — the per-metric catalog defaults apply.
      // TREND is always metric-scoped, so the rule does not apply there.
      const moduleCarriesResponse =
        metricEntry != null ||
        moduleHasMultipleMethods(snapshot.metricsCatalog, module, markets);
      const responses = metricEntry
        ? getResponses(metricEntry, out.scoreType)
        : moduleCarriesResponse
          ? getModuleResponses(
              snapshot.metricsCatalog,
              module,
              out.scoreType,
              markets,
            )
          : undefined;
      if (responses == null) {
        out.response = null;
      } else if (
        out.response == null ||
        !responses.some((r) => r.response === out.response)
      ) {
        out.response = metricEntry
          ? getDefaultResponse(metricEntry, out.scoreType)
          : getModuleDefaultResponse(
              snapshot.metricsCatalog,
              module,
              out.scoreType,
              markets,
            );
      }
    }
  }

  // ── questionOptions ────────────────────────────────────────────────────────
  // `questionOptions` is stored as Record<string, QuestionPickOption[]>.
  // Prune out any questionKey that is no longer present in allQuestionKeys.
  // Individual picks are passed through opaquely — their `type` is never
  // recomputed here (the three id sequences collide, so catalog membership
  // cannot resolve a pick's type).
  {
    if (
      snapshot.allQuestionKeys.length > 0 &&
      out.questionOptions != null &&
      Object.keys(out.questionOptions).length > 0
    ) {
      const pruned: Record<string, QuestionPickOption[]> = {};
      Object.entries(out.questionOptions).forEach(([questionKey, opts]) => {
        if (snapshot.allQuestionKeys.includes(questionKey)) {
          pruned[questionKey] = opts;
        }
      });
      out.questionOptions = pruned;
    }
  }

  // ── period granularity ────────────────────────────────────────────────────
  // Day and week pickers have parallel presets (L30D ↔ L4W, …); carry the
  // preset across instead of letting the other picker show it as custom.
  {
    const granularity = config?.datePickerGranularity;
    const main = convertPeriodForGranularity(
      out.periodType,
      granularity,
      config?.dateBounds,
    );
    if (main) {
      out.periodType = main.periodType;
      out.periodStartDate = main.startDate;
      out.periodEndDate = main.endDate;
    }
    const post = convertPeriodForGranularity(
      out.postPeriodType,
      granularity,
      config?.postDateBounds ?? config?.dateBounds,
    );
    if (post) {
      out.postPeriodType = post.periodType;
      out.postStartDate = post.startDate;
      out.postEndDate = post.endDate;
    }
  }

  // ── dateBounds / postDateBounds date clamping ─────────────────────────────
  // `config.dateBounds` is already fully resolved (bySubKey applied by
  // `resolveModuleConfig`) so a simple ISO-string comparison is sufficient.
  {
    const { min: dateMin, max: dateMax } = config?.dateBounds ?? {};
    if (dateMin) {
      if (out.periodStartDate && out.periodStartDate < dateMin)
        out.periodStartDate = dateMin;
      if (out.periodEndDate && out.periodEndDate < dateMin)
        out.periodEndDate = dateMin;
    }
    if (dateMax) {
      if (out.periodStartDate && out.periodStartDate > dateMax)
        out.periodStartDate = dateMax;
      if (out.periodEndDate && out.periodEndDate > dateMax)
        out.periodEndDate = dateMax;
    }
  }

  // Post-period date clamping: use postDateBounds if defined, else fall back to dateBounds.
  // This ensures post dates are always constrained by the module's date restrictions,
  // even when postDateBounds is not explicitly configured.
  {
    const effectivePostBounds = config?.postDateBounds ?? config?.dateBounds;
    const { min: postMin, max: postMax } = effectivePostBounds ?? {};
    if (postMin) {
      if (out.postStartDate && out.postStartDate < postMin)
        out.postStartDate = postMin;
      if (out.postEndDate && out.postEndDate < postMin)
        out.postEndDate = postMin;
    }
    if (postMax) {
      if (out.postStartDate && out.postStartDate > postMax)
        out.postStartDate = postMax;
      if (out.postEndDate && out.postEndDate > postMax)
        out.postEndDate = postMax;
    }
  }

  // ── statTest ───────────────────────────────────────────────────────────────
  // Validate `out.statTest` against `config.statTestingConfig`.
  //
  // Rules (aligned with StatTesting.tsx component behaviour):
  //   1. No `statTestingConfig` field (undefined)  → screen doesn't support stat
  //      testing at all → reset to "NONE".
  //   2. Empty `statTestingConfig` array ([])       → stat testing explicitly
  //      hidden on this screen → reset to "NONE".
  //   3. Non-empty config + current value is "NONE" → leave untouched (user
  //      deliberately turned stat testing off; that is always valid).
  //   4. Non-empty config + current value is active → walk the config to find
  //      the entry for the selected id:
  //        a. Entry missing from config entirely → not a valid option here.
  //        b. Entry is `hidden: true`            → not visible in the dropdown.
  //        c. Entry is `enabled: false`           → statically disabled by config.
  //      For cases (a)–(c) follow the `fallbackTo` chain until a valid
  //      (present, non-hidden, non-disabled) entry is found, then select it.
  //      If the chain exhausts without finding a valid target, reset to "NONE".
  //
  // Note: runtime disable conditions (`disableWhenDaysExceed`, `disableForPeriods`)
  // depend on the already-clamped date range and period.  Those are handled by
  // the StatTesting component's `useEffect` fallback at render time; we do NOT
  // attempt to evaluate them here to avoid circular dependencies between the
  // date-clamping block above and this block.
  {
    const statConfig = config.statTestingConfig;

    if (!statConfig || statConfig.length === 0) {
      // Cases 1 & 2: screen does not support (or has explicitly hidden) stat testing.
      out.statTest = "NONE";
    } else if (out.statTest && out.statTest !== "NONE") {
      // Case 4: an active stat test value is set — validate it against the config.

      /**
       * Resolves whether an entry (located by `id`) is structurally valid —
       * i.e. present in the config, not hidden, and not statically disabled.
       * Returns `true` when the option can legitimately be the selected value.
       */
      const isEntryValid = (id: string): boolean => {
        const entry = statConfig.find((e) => e.id === id);
        if (!entry) return false; // (a) not in config for this screen
        if (entry.hidden === true) return false; // (b) hidden from the dropdown
        if (entry.enabled === false) return false; // (c) statically disabled
        return true;
      };

      /**
       * Walks the `fallbackTo` chain starting from `startId`, returning the
       * first id whose entry is valid, or `"NONE"` if the chain is exhausted
       * or no valid target is found.  A visited-set guards against cycles.
       */
      const resolveFallback = (startId: string): string => {
        const visited = new Set<string>();
        let currentId: string | undefined = startId;
        while (currentId && !visited.has(currentId)) {
          visited.add(currentId);
          const entry = statConfig.find((e) => e.id === currentId);
          if (!entry) break; // entry not in config — chain broken
          if (isEntryValid(currentId)) return currentId;
          currentId = entry.fallbackTo;
        }
        // Fallback chain exhausted: pick first valid option from the config, or "NONE".
        const firstValid = statConfig.find((e) => isEntryValid(e.id));
        return firstValid?.id ?? "NONE";
      };

      if (!isEntryValid(out.statTest)) {
        // Current value is invalid — follow the fallback chain.
        const currentEntry = statConfig.find((e) => e.id === out.statTest);
        out.statTest = resolveFallback(currentEntry?.fallbackTo ?? "");
      }
      // else: current value is valid — leave it unchanged.
    }
    // Case 3: out.statTest === "NONE" — always valid; nothing to do.
  }

  return out;
}

// ─── Listener middleware ──────────────────────────────────────────────────────

export const selectionListenerMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;

const startListening =
  selectionListenerMiddleware.startListening as AppStartListening;

/**
 * Fires synchronously (before any React re-render) on every `setPendingSelection`
 * dispatch. Executes the full merge → resolve → prune → commit cycle and
 * resets the ephemeral `pendingSelection` slice when done.
 */
startListening({
  actionCreator: setPendingSelection,
  effect: async (_action, listenerApi) => {
    // Cancel any previous still-running instance of this listener so that
    // only the most-recent `setPendingSelection` dispatch is processed.
    listenerApi.cancelActiveListeners();
    try {
    // ── Guard: wait for filters + segments to be ready ─────────────────────
    // `FetchFiltersAndSegments` sets filtersReady + segmentsReady after the
    // initial market fetch completes.  Navigation proceeds as soon as both
    // flags are true — we do NOT gate on `questionsReady` here because doing
    // so creates a deadlock when navigating TO the Exclusive Questions page:
    // the listener would wait for `questionsReady` before calling navigateTo,
    // but `questionsReady` is only set by ExclusiveQuestions.tsx after it
    // mounts, which never happens until navigateTo fires.
    // `pruneInvalidFields` already safely skips the questionOptions prune when
    // `allQuestionKeys.length === 0`, so the gate is not needed.
    const isDataReady = (s: RootState): boolean => {
      return (
        s.pendingSelection.filtersReady === true &&
        s.pendingSelection.segmentsReady === true
      );
    };

    if (!isDataReady(listenerApi.getState())) {
      const didBecomeReady = await listenerApi.condition(
        (_act, currentState) => isDataReady(currentState),
        30_000, // 30 s safety timeout
      );
      if (!didBecomeReady) {
        // Timed out – proceed anyway rather than leaving the selection hung.
        if (import.meta.env.DEV) {
          console.warn(
            "[selectionListener] Timed out waiting for filtersReady / segmentsReady",
          );
        }
      }
    }

    // Re-read state after the potential async wait so we have fresh values.
    const state = listenerApi.getState();

    // ── Merge ──────────────────────────────────────────────────────────────
    // Produce the effective selection using a 3-tier priority:
    //   1. pendingSelection (highest) — explicit user / navigation changes
    //   2. screenLevelSelection[contextKey] — per-screen persisted context
    //      (only brands / markets / segments / secondaryBrands fields)
    //   3. userSelection (base) — current committed selection
    //
    // The context key is derived from the *resolved* screen / mode / vizType
    // (pending values shadow userSelection values for this lookup).
    const _screenType =
      state.pendingSelection.screenType ?? state.userSelection.screenType ?? "";
    const _mode = state.pendingSelection.mode ?? state.userSelection.mode ?? "";
    // When the module is changing and the pending selection does not explicitly
    // include a visualizationType, do NOT carry over the current viz type from
    // userSelection. Doing so would look up a viz-specific context key (e.g.
    // "brand|POINT_IN_TIME|DIFFERENTIATION") that belongs to the PREVIOUS
    // module, restoring only the single primary brand instead of the full
    // multi-brand selection stored under the base "brand" key.
    // Example: Brand Visibility (brands=[1,2,3,4]) → Emotional Differentiation
    // (brands=[1], secondaryBrands=[2,3,4]) → EQ ByBrand. Without this guard,
    // _contextKey = "brand|POINT_IN_TIME|DIFFERENTIATION" → brands=[1] (wrong).
    // With this guard, _contextKey = "brand" → brands=[1,2,3,4] (correct).
    const _pendingModule = state.pendingSelection.module;
    const _moduleChanging =
      _pendingModule != null &&
      normaliseModuleToken(_pendingModule) !==
        normaliseModuleToken(state.userSelection.module);
    const _vizType =
      state.pendingSelection.visualizationType ??
      (_moduleChanging ? "" : (state.userSelection.visualizationType ?? ""));
    const _contextKey = buildContextKey(
      _screenType,
      _mode,
      _vizType,
      _pendingModule ?? state.userSelection.module,
    );
    const _screenLevelCtx = _contextKey
      ? (state.screenLevelSelection[_contextKey] ?? {})
      : {};
    const effective = mergeSelections(
      state.userSelection,
      state.pendingSelection,
      _screenLevelCtx,
    );

    if (effective.module === "IMAGERY") {
      effective.module = "EMOTIONAL";
    }
    // SideNav and ModulePage send the route segment "aivisibility". The
    // registry key is AI_VISIBILITY (underscore). A raw toUpperCase() lookup
    // misses, screenConfig stays empty, and the module view renders nothing.
    if (
      effective.module?.toLowerCase().replace(/[-_]/g, "") === "aivisibility"
    ) {
      effective.module = "AI_VISIBILITY";
    }

    // ── Screen-switch segment/filter reset ────────────────────────────────
    // When the user navigates to a *different* screenType, always clear
    // segments and filters carried over from the previous screen.
    // The target screen's prune sets them correctly:
    //   • SEGMENT_ONLY  → activeSegmentIds
    //   • SEGMENT_WITH_FILTERS / default → [] (zero is valid)
    // We do NOT restore from screenLevelSelection here because the reducer's
    // propagation logic can incorrectly mirror segment-page IDs onto other keys
    // when the segment screenType key doesn't start with the literal "segment".
    {
      const prevScreenType = state.userSelection.screenType;
      const nextScreenType = effective.screenType ?? "";
      if (prevScreenType !== "" && prevScreenType !== nextScreenType) {
        if (!state.pendingSelection?.segments?.length) {
          effective.segments = [];
        }

        // ── "Select all" defaulting on first visit to a screen ──────────
        // When landing on byMarket or byBrand for the first time (no
        // persisted screenLevelSelection context for that screen), clear the
        // entity arrays that carried over from the previous screen so that
        // pruneInvalidFields defaults to ALL available IDs:
        //
        //   • byMarket (no maximumAllowedMarkets) → all market IDs
        //   • byBrand  (no maximumAllowedBrands)  → all brand IDs in market
        //
        // This guard is skipped when the caller explicitly provided those
        // fields in the pending dispatch (e.g. a URL-encoded navigation with
        // market/brand params already present).
        const savedCtx = state.screenLevelSelection[_contextKey];

        // A context is considered "existing" only when it carries a non-empty
        // list for the dimension that matters on the target screen.
        const hasExistingMarketCtx = (savedCtx?.markets?.length ?? 0) > 0;
        const hasExistingBrandCtx = (savedCtx?.brands?.length ?? 0) > 0;

        // Detect whether the pending dispatch explicitly supplied these dims.
        const pendingHasMarkets =
          Array.isArray(state.pendingSelection.markets) &&
          state.pendingSelection.markets.length > 0;
        const pendingHasBrands =
          Array.isArray(state.pendingSelection.brands) &&
          state.pendingSelection.brands.length > 0;

        // byMarket → clear markets → prune seeds ALL available markets
        if (
          nextScreenType === "market" &&
          !hasExistingMarketCtx &&
          !pendingHasMarkets
        ) {
          effective.markets = [];
        }

        // byBrand → clear brands → prune seeds ALL brands for selected market
        if (
          nextScreenType === "brand" &&
          !hasExistingBrandCtx &&
          !pendingHasBrands
        ) {
          effective.brands = [];
        }
  }
}

// ── Clear segments when navigating FROM report builder ────────────────
// Report builder has `supportedURLKeys: []` so segments are never
// serialised to its URL. When the user was on a segment screen before
// visiting report builder, the screen-switch reset already cleared
// `effective.segments` and the commit block wrote `segments: []` to
// `userSelection`. However, if the user then navigates from report
// builder to a brand/market screen, `prevScreenType === nextScreenType`
// ("brand" → "brand") so the screen-switch reset above does NOT fire,
// and any segments that leaked through (e.g. from screenLevelSelection)
// would be applied on the brands page. Explicitly clearing here ensures
// a clean slate whenever the user leaves report builder.
{
  const _prevModuleForReportsCheck = normaliseModuleToken(state.userSelection.module);
  const _nextModuleForReportsCheck = normaliseModuleToken(effective.module);
  if (
    _prevModuleForReportsCheck === "REPORTS" &&
    _nextModuleForReportsCheck !== "REPORTS" &&
    !state.pendingSelection?.segments?.length
  ) {
    effective.segments = [];
  }
}

    // ── Drop a distribution category the new module cannot show ─────────────
    // A module-linked category (Equity, Conversion Funnel …) exists only on its
    // own module's picker; the ad-hoc ones (Demographics, Other Brand,
    // Additional Profiles) exist everywhere. Carrying a foreign one into the
    // new module's URL does not merely look wrong — it breaks the navigation
    // outright: the Profile view re-resolves it to a valid category and
    // dispatches the correction, that dispatch carries no `module`, and it
    // merges against the module still committed in `userSelection`, navigating
    // straight back to the module the user just left.
    //
    // Clearing it here makes that state unrepresentable, so no correction is
    // ever needed. Runs before config resolution because `profileMetric` is the
    // subKey that resolves `dateBounds`.
    {
      const distributionsCatalog =
        state.activeGroup?.distributionsCatalog?.catalog ?? null;
      const prevModule = normaliseModuleToken(state.userSelection.module);
      const nextModule = normaliseModuleToken(effective.module);

      if (
        distributionsCatalog != null &&
        effective.profileMetric != null &&
        prevModule !== "" &&
        nextModule !== "" &&
        prevModule !== nextModule
      ) {
        const visible = getCategoriesForModule(
          distributionsCatalog,
          nextModule,
        ).some((c) => c.key === effective.profileMetric);
        if (!visible) {
          effective.profileMetric = null;
          effective.profileResponse = null;
        }
      }
    }

    // ── Resolve config ─────────────────────────────────────────────────────
    // Derive the flat CONFIG_SCHEMA by walking the hierarchical ModuleConfig.
    // Falls back to the legacy flat store lookup when no registry entry exists.
    const _moduleConfig =
      MODULE_CONFIG_REGISTRY[effective.module?.toUpperCase()];
    let config: CONFIG_SCHEMA | undefined = _moduleConfig
      ? resolveModuleConfig(
          _moduleConfig,
          effective.screenType?.toUpperCase() as ScreenTypeName,
          effective?.mode as any,
          effective?.visualizationType as any,
          // 5th arg: subKey for dateBounds / postDateBounds bySubKey resolution.
          // profileMetric is used for DISTRIBUTION vizType (Visibility/Reputation profile-metric
          // overrides); trendsMetric is used for TREND mode (e.g. adRecall in Visibility).
          // resolveModuleConfig's resolveSubKey() chooses the right entry from bySubKey.
          effective.visualizationType === "DISTRIBUTION"
            ? effective.profileMetric
            : effective.mode === "TREND"
              ? effective.trendsMetric
              : null,
          effective.visualizationType === "DISTRIBUTION"
            ? effective.profileResponse
            : null,
        )
      : undefined;

    // ── Category-filtered brand reset on module change ──────────────────────
    // The screen-switch reset above only fires when screenType itself changes,
    // so it misses Equity (byBrand) → AI Visibility (byBrand): both resolve to
    // screenType "brand", so Equity's selected brands leak into
    // `effective.brands` via mergeSelections' `current` tier. If one of those
    // brands happens to belong to AI Visibility's default category it survives
    // pruneInvalidFields' category filter, landing with only that one carried-
    // over brand instead of "all brands in the category" (AI Visibility has no
    // maximumAllowedBrands). Only reset when the target module's own
    // screen-level context (already resolved above as `_screenLevelCtx`) has no
    // brands of its own and the navigation didn't explicitly request brands —
    // a real screen-level context must still win.
    if (
      _moduleChanging &&
      config?.isBrandsFilteredByCategory &&
      effective.screenType === "brand"
    ) {
      const hasExistingBrandCtx = (_screenLevelCtx.brands?.length ?? 0) > 0;
      const pendingHasBrands =
        Array.isArray(state.pendingSelection.brands) &&
        state.pendingSelection.brands.length > 0;
      if (!hasExistingBrandCtx && !pendingHasBrands) {
        effective.brands = [];
      }
    }

    // ── Resolve the trendsMetric used for config overrides ───────────────────
    // The metric axis is validated against the metric catalog in
    // `pruneInvalidFields`, so the effective value is already in range here.
    const resolvedTrendsMetric = effective.trendsMetric;

    // ── Apply market-based base overrides ──────────────────────────────────
    // If all selected markets share the same name and that name has an entry
    // in MARKETS_BASE_OVERRIDES, narrow supportedBases / defaultBase to the
    // override values.  This allows per-market-type base restrictions (e.g.
    // ETSY markets only support GENPOP / FAMILIAR / AWARE).
    // NOTE: This runs before the trends-metric override so that the
    // trends-metric override can further narrow the bases if needed.
    if (config != null && (effective.markets ?? []).length > 0) {
      const marketsEntityState = state.activeGroup.markets?.markets;
      const selectedMarketNames: string[] = (effective.markets as number[]).map(
        (id) => marketsEntityState?.entities?.[id]?.groupName ?? "",
      );
      const allSameName =
        selectedMarketNames.length > 0 &&
        selectedMarketNames.every((n) => n === selectedMarketNames[0]);
      if (allSameName) {
        const marketName = selectedMarketNames[0].toUpperCase();
        const marketBaseOverride = MARKETS_BASE_OVERRIDES[marketName];
        if (marketBaseOverride != null) {
          config = {
            ...config,
            supportedBases: marketBaseOverride.supportedBases,
            defaultBase: marketBaseOverride.defaultBase,
          };
        }
      }
    }

    // ── Apply TREND-mode base overrides per trendsMetric ───────────────────
    if (
      config != null &&
      effective.mode?.toUpperCase() === "TREND" &&
      resolvedTrendsMetric != null &&
      (TRENDS_METRIC_BASE_OVERRIDES[resolvedTrendsMetric] != null ||
        TRENDS_METRIC_BASE_OVERRIDES[
          `${effective.module?.toUpperCase()}_${resolvedTrendsMetric}`
        ] != null)
    ) {
      const baseOverride =
        TRENDS_METRIC_BASE_OVERRIDES[resolvedTrendsMetric] ||
        TRENDS_METRIC_BASE_OVERRIDES[
          `${effective.module?.toUpperCase()}_${resolvedTrendsMetric}`
        ];
      config = {
        ...config,
        supportedBases: baseOverride.supportedBases,
        defaultBase: baseOverride.defaultBase,
      };
    }

    if (!config) {
      // No config registered yet (screen not yet mounted).
      // Commit the merged state as-is so selection is not lost.
      listenerApi.dispatch(syncFromURL(effective));
      listenerApi.dispatch(clearPendingSelection());
      return;
    }
    // ── Prune invalid fields ───────────────────────────────────────────────
    // Build the available-IDs snapshot, then prune the merged state.
    const snapshot = buildActiveGroupSnapshot(state, effective, config);
    // ── Seed secondaryBrands when switching to a secondary-brands viz ───────────
    // When the user switches TO a visualisation type that supports secondary
    // brands (DIFFERENTIATION / BUTTERFLY) and there is no saved context for
    // that viz yet, the previous viz may have committed only a single brand
    // (e.g. BUTTERFLY caps to 1).  In that case we synthesise a sensible
    // starting point so the user sees a meaningful comparison immediately:
    //
    //   • effective.brands already contains multiple brands (BAR / BUBBLE /
    //     DISTRIBUTION / PERCEPTUAL) → use those directly.
    //   • Otherwise fall back to the base screenLevelSelection slot keyed by
    //     screenType alone — that slot is always written by multi-brand vizzes
    //     and retains the full selection even when the previous viz (e.g.
    //     BUTTERFLY, max 1 brand) only committed a single brand.
    {
      const prevVizType = state.userSelection.visualizationType ?? "";
      const nextVizType = effective.visualizationType ?? "";

      const vzTypeSwitched = prevVizType !== nextVizType;
      const targetSupportsSecondaryBrands =
        config.supportSecondaryBrands === true;
      // Context is absent when the key has neither brands nor secondaryBrands
      // (first-ever visit to this viz type, or context was cleared).
      const noSavedCtxForTarget =
        !_screenLevelCtx.brands?.length &&
        !_screenLevelCtx.secondaryBrands?.length;

      if (
        vzTypeSwitched &&
        targetSupportsSecondaryBrands &&
        noSavedCtxForTarget
      ) {
        // Determine the source brands for the split.
        // If effective.brands already contains multiple brands (coming from a
        // multi-brand viz like BAR / BUBBLE / DISTRIBUTION / PERCEPTUAL), use those
        // directly.  Otherwise fall back to the base screenLevelSelection slot
        // keyed by screenType alone — that slot is always written by BAR/BUBBLE/
        // DISTRIBUTION/PERCEPTUAL and retains the full multi-brand selection even
        // when the previous viz (e.g. BUTTERFLY, max 1 brand) only committed
        // a single brand to userSelection.
        const baseScreenCtx = state.screenLevelSelection[_screenType] ?? {};
        const sourceBrands: (string | number)[] =
          effective.brands.length > 1
            ? effective.brands
            : (baseScreenCtx.brands?.length ?? 0) > 1
              ? (baseScreenCtx.brands as (string | number)[])
              : effective.brands;

        if (sourceBrands.length > 1) {
          // Seed effective.brands from the full source so pruneInvalidFields
          // caps to maximumAllowedBrands (1 for BUTTERFLY / DIFFERENTIATION).
          effective.brands = sourceBrands;
          // Promote brands[1..] to secondaryBrands; pruneInvalidFields will cap:
          //   • brands          → maximumAllowedBrands (1 for DIFFERENTIATION/BUTTERFLY)
          //   • secondaryBrands → maximumAllowedSecondaryBrands (1 for BUTTERFLY,
          //                       uncapped / 4 for DIFFERENTIATION)
          effective.secondaryBrands = sourceBrands.slice(1);
        }
      }
    }

    // ── Reset the scoring axes on a MODULE change ──────────────────────────
    // Scoring is module-scoped: the method (`scoreType`) and its slice
    // (`response`) mean different things per module, so carrying the previous
    // module's pair across a navigation is never right — Reputation must land
    // on its own default method (Mean) even when arriving from a module whose
    // method also happens to exist in the catalog.
    //
    // Clearing both axes here (rather than assigning a default inline) hands
    // the decision to the single catalog-driven resolver in
    // `pruneInvalidFields`: an empty method is never a valid catalog method, so
    // the prune snaps it to the metric default (TREND) or the module default
    // (every other mode), then resolves the matching response.
    //
    // Two facts make this comparison load-bearing — do not "simplify" either:
    //
    //   • `useURLToStore` commits the module (derived from the pathname) via
    //     `syncFromURL` BEFORE the bootstrap pending dispatch runs. On cold
    //     entry — hard refresh, Home redirect, a saved-view tab — the module
    //     therefore already matches and this block correctly does NOT fire.
    //     The module default is still reached, because `userSelection`'s
    //     initial `scoreType` is `""` (unresolved) and the prune snaps it.
    //     That same ordering is what protects a deep link's explicit URL
    //     `scoreType`: it lands in `userSelection` before any pending dispatch,
    //     so no module change is ever observed for it.
    //   • `toUpperCase()` is required, not cosmetic: `ModulePage.tsx` (:60-63)
    //     dispatches a LOWERCASE module on every tab change within a module,
    //     while `userSelection.module` may hold either case. Comparing raw
    //     values would read every tab change as a module change.
    //
    // The explicit-`scoreType` carve-out below is a safety net rather than a
    // live path: `setPendingSelectionIfChanged` strips unchanged keys, and no
    // production dispatch pairs a `scoreType` with a module change today. It is
    // kept so a future "restore this exact pair" caller is honoured — such a
    // value still goes through catalog validation in the prune, so an invalid
    // explicit pair is corrected, not trusted.
    //
    // Runs only once the catalog is loaded; the prune's own catalog guard would
    // otherwise pass the cleared value straight through and commit "".
    const scoreTypeBeforeModuleReset = effective.scoreType;
    let didResetScoringAxes = false;
    {
      const prevModule = normaliseModuleToken(state.userSelection.module);
      const nextModule = normaliseModuleToken(effective.module);
      const pendingHasExplicitScoreType =
        state.pendingSelection.scoreType != null;

      if (
        snapshot.metricsCatalog != null &&
        prevModule !== "" &&
        nextModule !== "" &&
        prevModule !== nextModule &&
        !pendingHasExplicitScoreType
      ) {
        effective.scoreType = "";
        effective.response = null;
        didResetScoringAxes = true;
      }
    }

    // Entering DISTRIBUTION from another visualization starts on the module's own
    // distribution category. The carried `profileMetric` is usually still valid
    // — the ad-hoc categories are offered on every module — so without this the
    // user lands on whatever they last looked at rather than on the module they
    // navigated to. Switching module while already in DISTRIBUTION does not reset,
    // so a category that survives the move is kept.
    //
    // An explicit `profileMetric` in the payload wins, so a shared or bookmarked
    // Profile link opens on the category it names. That value is still resolved
    // against the catalog, so an invalid or out-of-market one is corrected
    // rather than trusted.
    {
      const prevVizType = state.userSelection.visualizationType ?? "";
      const nextVizType = effective.visualizationType ?? "";
      const pendingHasExplicitProfileMetric =
        state.pendingSelection.profileMetric != null;

      if (
        nextVizType === "DISTRIBUTION" &&
        prevVizType !== "DISTRIBUTION" &&
        !pendingHasExplicitProfileMetric
      ) {
        effective.profileMetric = null;
        effective.profileResponse = null;
      }
    }

    const cleanState = pruneInvalidFields(effective, config, snapshot);

    // The cleared method survives the prune only when the catalog offers this
    // module no methods at all (module absent from the catalog / no
    // market-eligible metrics). Committing "" would be worse than the carried
    // value, so put the previous METHOD back.
    //
    // Only the method: `response` stays as the prune left it (null). Restoring
    // the previous module's response alongside would re-introduce exactly the
    // invalid pair this change exists to make unreachable — a slice belonging
    // to a method the new module cannot express.
    if (didResetScoringAxes && cleanState.scoreType === "") {
      cleanState.scoreType = scoreTypeBeforeModuleReset;
    }

    // ── Re-prune markets when brand changed during pruning ─────────────────
    // When `isMarketsFilteredByBrand` is true and the brand changed during
    // pruning (e.g. the carried-over brand was not reputation-eligible and was
    // replaced by the first eligible brand from `isBrandsFilteredByOwnReputationMarkets`),
    // the market pool was computed from the OLD brand's `marketIds` in the
    // snapshot above.  We must rebuild the snapshot with the NEW brand so that
    // `brandFilteredMarketIds` reflects the correct brand, then re-prune markets.
    if (
      config.isMarketsFilteredByBrand &&
      String(cleanState.brands[0]) !== String(effective.brands[0])
    ) {
      const newSnapshot = buildActiveGroupSnapshot(state, cleanState, config);
      const marketPool = newSnapshot.brandFilteredMarketIds;
      // Iterate the pool (not cleanState.markets) so the result preserves
      // the canonical snapshot order, not the user/URL order.
      const userMarketSet = new Set(cleanState.markets.map(String));
      let markets = marketPool.filter((id) => userMarketSet.has(String(id)));
      if (config.maximumAllowedMarkets != null) {
        markets = markets.slice(0, config.maximumAllowedMarkets);
      }
      if (markets.length === 0 && marketPool.length > 0) {
        const limit = config.maximumAllowedMarkets ?? marketPool.length;
        markets = marketPool.slice(0, limit);
      }
      if (
        config.minimumRequiredMarkets != null &&
        markets.length < config.minimumRequiredMarkets &&
        marketPool.length > 0
      ) {
        const needed = config.minimumRequiredMarkets - markets.length;
        const existingSet = new Set(markets);
        const candidates = marketPool.filter((id) => !existingSet.has(id));
        markets = [...markets, ...candidates.slice(0, needed)];
      }
      cleanState.markets = markets;

      // `cleanState.markets` just changed after `pruneInvalidFields` already
      // gated filters/segments — gate them again against the list actually
      // being committed.
      const regated = pruneSelectionsForMarketApplicability(
        cleanState.filters as unknown as FilterSelection[],
        cleanState.segments,
        cleanState.markets,
        newSnapshot,
      );
      cleanState.filters =
        regated.filters as unknown as typeof cleanState.filters;
      cleanState.segments = regated.segments;
    }

    // One subtle, non-blocking notice when market applicability removed part
    // of the user's applied selection (HVXB-122). `effective` still holds the
    // pre-prune filters/segments; `cleanState.markets` is the FINAL market
    // list (the brand-reseed re-prune above may have reassigned it after
    // `pruneInvalidFields` ran).
    const marketPruneNotice = describeMarketPrune(
      {
        filters: effective.filters as unknown as FilterSelection[],
        segments: effective.segments,
      },
      cleanState.markets,
      snapshot,
    );

    // ── Strip brand-set ULIDs from URL when feature is disabled ───────────
    // When featureStatus.brandSet.enabled is false, brand-set IDs (non-numeric
    // string ULIDs) must not appear in the URL. Filter them out of brands and
    // secondaryBrands before serialising to URLSearchParams.
    const brandSetEnabled =
      (state.featureStatus as any)?.brandSet?.enabled === true;
    if (!brandSetEnabled) {
      const isNumericId = (id: number | string): boolean =>
        typeof id === "number" || !isNaN(Number(id));
      cleanState.brands = cleanState.brands.filter(isNumericId);
      cleanState.secondaryBrands =
        cleanState.secondaryBrands.filter(isNumericId);
    }

    // ── Commit fields the URL round-trip would drop ────────────────────────
    // For a registered module the listener does NOT commit `cleanState` to the
    // store — it navigates, and `useURLToStore` parses the new URL and commits
    // it via `syncFromURL`. That reducer only writes the keys PRESENT in its
    // payload, so any field the target screen does not serialise into the URL
    // silently keeps the PREVIOUS screen's value.
    //
    // The scoring axes are exactly that case:
    //   • `scoreType` was in `supportedURLKeys` for Reputation only (the sole
    //     multi-method module), so navigating Reputation → Equity dropped the
    //     pruned method on the floor and left MEAN committed on a module where
    //     every metric is PERCENTAGE-only. `scoreType` now has near-universal
    //     `supportedURLKeys` coverage (Saved Views V2 widening — see the per-module
    //     `config/*.config.ts` files), so most navigations round-trip it through
    //     the URL on their own; this commit stays as the fallback for any
    //     screen a future config edit leaves without the key, and it is what
    //     still closes the hole for `response` below.
    //   • `response` resolves to `null` whenever the method carries no response
    //     menu, and a null is never serialised, so a stale `POSITIVE` survived
    //     the same way — the two together reached the wire as an
    //     unrepresentable pair.
    //
    // The distribution category is the same case for the same reason: entering
    // DISTRIBUTION clears `profileMetric` to null so it re-resolves to the module's
    // own category, and a null is never serialised, so the stale category would
    // survive the round trip and the reset would do nothing.
    //
    // `brands`/`secondaryBrands` hit the identical hole whenever pruning empties
    // them out (e.g. switching AI category to one that shares no brands with the
    // previous selection): `buildURLFromSelection` appends nothing for an empty
    // array, so the `brands` param is entirely ABSENT from the URL rather than
    // present-and-empty, `parseURLParams` never produces a `brands` key for an
    // absent param, and `syncFromURL` only overwrites keys present in its
    // payload — so the stale, pre-prune brand ids silently survive in
    // `userSelection.brands` even though the Brands dropdown (which re-derives
    // its options from the new category) renders as if nothing were selected.
    // Downstream fetches then keep requesting the OLD brands under the NEW
    // category, rendering that stale response instead of a "No data" state.
    //
    // `models` hits the same hole the moment it can be cleared back to `[]`
    // — e.g. a mode/screenType change landing on a screen that doesn't
    // support the model filter at all — an empty array never serialises
    // into the URL either, so the stale model id(s) would otherwise stay
    // committed and the dropdown/label would appear stuck.
    //
    //   • `markets` must travel with `brands`: the brands above were pruned
    //     against the NEW markets, so committing them alone leaves a window
    //     (until `useURLToStore` syncs the URL) where `userSelection` pairs the
    //     new brands with the OLD markets. Page side-effects that validate
    //     brands against markets (e.g. `useReputationBrandFilter`) read that
    //     inconsistent pair, dispatch a correction carrying no `markets`, and
    //     the listener merges it against the old markets — reverting the
    //     user's market change (Age 18-64 → US National on Reputation).
    //
    //   • `segments` is the same case for modules whose `supportedURLKeys` does
    //     not include "segments" (e.g. Report Builder with `supportedURLKeys: []`).
    //     When the screen-switch reset clears segments (e.g. Segments tab →
    //     Report Builder), `useURLToStore` skips the `syncFromURL` segments
    //     update because `segmentsSupported = false`, leaving the stale segment
    //     IDs in `userSelection`. The next module navigation then picks them up
    //     from `current` in `mergeSelections` and incorrectly applies them on
    //     the By-Brand page. Committing the pruned value here closes that hole.
    //
    // Committing them straight to `userSelection` closes the round-trip hole.
    // The later `syncFromURL` from `useURLToStore` is harmless: it either
    // carries the identical value or omits the key entirely.
    {
      const commit: Partial<UserSelectionState> = {};
      if (cleanState.scoreType !== state.userSelection.scoreType) {
        commit.scoreType = cleanState.scoreType;
      }
      if (cleanState.response !== state.userSelection.response) {
        commit.response = cleanState.response;
      }
      if (cleanState.profileMetric !== state.userSelection.profileMetric) {
        commit.profileMetric = cleanState.profileMetric;
      }
      if (cleanState.profileResponse !== state.userSelection.profileResponse) {
        commit.profileResponse = cleanState.profileResponse;
      }
      if (!dequal(cleanState.brands, state.userSelection.brands)) {
        commit.brands = cleanState.brands;
      }
      if (!dequal(cleanState.markets, state.userSelection.markets)) {
        commit.markets = cleanState.markets;
      }
      if (!dequal(cleanState.secondaryBrands, state.userSelection.secondaryBrands)) {
        commit.secondaryBrands = cleanState.secondaryBrands;
      }
      if (!dequal(cleanState.models, state.userSelection.models)) {
        commit.models = cleanState.models;
      }
      if (!dequal(cleanState.segments, state.userSelection.segments)) {
        commit.segments = cleanState.segments;
      }
      if (Object.keys(commit).length > 0) {
        listenerApi.dispatch(syncFromURL(commit));
      }
    }

    // ── Build URL and navigate ────────────────────────────────────────────
    // Serialise only the fields declared in `config.supportedURLKeys` into
    // the browser URL. Derive the pathname from module + screenType so that
    // tab changes (same module, different screenType) and cross-module
    // navigations (SideNav clicks) are handled entirely by the listener —
    // components only need to dispatch `setPendingSelection`.
    const urlParams = buildURLFromSelection(
      cleanState,
      config.supportedURLKeys ?? [],
    );
    let targetPathname = "";
    if (cleanState.module === "reports") {
      targetPathname = cleanState.module
        ? `/${cleanState.module?.toLowerCase()}`
        : window.location.pathname;
    } else {
      // Route segment aliases (registry / userSelection token → URL path).
      const modulePathSegment =
        cleanState.module === "AI_VISIBILITY"
          ? "aivisibility"
          : cleanState.module?.toLowerCase();
      targetPathname =
        cleanState.module && cleanState.screenType
          ? `/${modulePathSegment}/${cleanState.screenType}`
          : window.location.pathname;
    }

    const targetSearch = urlParams.toString();
    const isSamePath =
      window.location.pathname === targetPathname &&
      window.location.search === (targetSearch ? `?${targetSearch}` : "");

    if (isSamePath) {
      const currentScreenConfig = (state as RootState).screenConfig;
      if (!dequal(currentScreenConfig, config)) {
        listenerApi.dispatch(setScreenConfig(config));
      }
      listenerApi.dispatch(clearPendingSelection());
      // No navigation occurs on same-path clicks (e.g. clicking the active
      // module logo in the SideNav). `useModeViewData` will NOT re-run its
      // fetch cycle, so `clearPendingAPIChange` will never be dispatched from
      // there. Dispatch it here so `loading` is not left stuck at `true`,
      // which would cause an infinite spinner.
      listenerApi.dispatch(clearPendingAPIChange());
      return;
    }

    // Toast only once we know navigation actually changes the visible URL —
    // a prune that lands on the same path/search produced no visible change
    // and returned above, so it must never surface a notice.
    if (marketPruneNotice) {
      ShowToaster.info(marketPruneNotice);
    }


    navigateTo(
      { pathname: targetPathname, search: targetSearch },
      { replace: true },
    );

    // ── Propagate config updates → screenConfig ───────────────────────────
    listenerApi.dispatch(setScreenConfig(config));

    // ── Reset ephemeral pending slice ──────────────────────────────────────
    listenerApi.dispatch(clearPendingSelection());
    } catch (err) {
      // `cancelActiveListeners()` in a newer run rejects this run's pending
      // `condition()`. The newer run owns `pendingSelection` (it still holds
      // this run's fields, e.g. `markets`), so clearing it here would drop
      // them — a market pick followed instantly by a SideNav click lost both.
      if (listenerApi.signal.aborted) return;
      // A throw here aborts before navigate/syncFromURL, so data APIs never
      // fire and `loading` stays true forever. Clear the flag and surface the
      // error so the UI can recover on the next selection change.
      // eslint-disable-next-line no-console
      console.error("[selectionListener] effect failed — data fetch blocked", err);
      listenerApi.dispatch(clearPendingAPIChange());
      listenerApi.dispatch(clearPendingSelection());
    }
  },
});
